/**
 * National location taxonomy (Feb 2026 — SEO pilot).
 *
 *   country → state → region → suburb
 *
 * Only Australia is modelled. Only Queensland has a live state hub;
 * only Sunshine Coast has an active regional hub. Suburbs still live
 * in `locations.json` (the flat, per-suburb content source) — this
 * file is the taxonomy that stitches them into a hierarchy for URLs
 * and hub pages.
 *
 * Suburb slug convention:
 *   • Canonical (new): `/locations/<state>/<region>/<suburb-short-slug>`
 *     e.g. `/locations/queensland/sunshine-coast/doonan`
 *   • Legacy `/locations/<suburb>-water-testing` still resolves via a
 *     301 redirect handled inside `LocationLanding`.
 *
 * The `suburb_short_slug_of()` helper below is the single source of
 * truth for producing the canonical short slug from a locations.json
 * `slug` field.
 */

export const COUNTRIES = {
  australia: {
    slug: "australia",
    name: "Australia",
    states: ["queensland", "new-south-wales", "victoria", "northern-territory"],
    tagline: "Independent, laboratory-certified water testing for every Australian home.",
  },
};

export const STATES = {
  queensland: {
    slug: "queensland",
    country: "australia",
    name: "Queensland",
    abbr: "QLD",
    tagline:
      "From coastal rainwater tanks to bore-fed acreage, we test every water source in Queensland to Australian Drinking Water Guidelines.",
    regions: ["sunshine-coast", "toowoomba", "dayboro", "redland-bay-logan", "tropical-north-queensland", "gold-coast-south-north", "cooloola-fraser-coast"],
    // Genuinely local context — no name-swap boilerplate. Used by the
    // Queensland hub for the "About water in this state" section.
    context: {
      dominant_sources: [
        "Rainwater tanks (compulsory on all new SEQ dwellings)",
        "Private bores and spear points across sandy hinterland",
        "Town water in urban corridors (SEQ Water, Unity Water, Cairns Regional Water)",
        "Dam and creek water on rural properties (irrigation and stock)",
      ],
      common_concerns: [
        "Bacterial breakthrough after wet-season roof washdown",
        "Manganese and iron in shallow QLD bores",
        "Low pH / soft rainwater corroding copper and hot-water systems",
        "PFAS legacy zones near Defence and Rural Fire Service training sites",
        "Sediment and turbidity after cyclonic rainfall",
      ],
      regulations: [
        "Queensland Water Supply Act (Safety and Reliability) 2008",
        "SEQ Drinking Water Quality Management Plan (drinking-water suppliers)",
        "Australian Drinking Water Guidelines (ADWG) 2011, updated 2022",
      ],
    },
  },
  "new-south-wales": {
    slug: "new-south-wales",
    country: "australia",
    name: "New South Wales",
    abbr: "NSW",
    tagline:
      "From high-rainfall Northern Rivers tanks to Central West bores and Sydney town supply, we test every NSW water source to Australian Drinking Water Guidelines.",
    regions: ["northern-rivers", "tweed-byron", "riverina-southern-tablelands", "central-coast", "bathurst-central-tablelands"],
    // Genuinely local context for the NSW state hub. Sources cross-
    // referenced against NSW Health Private Water Supply Guidelines,
    // NSW EPA PFAS mapping, and Rous County Council / Hunter Water /
    // Sydney Water source docs. Deliberately non-boilerplate.
    context: {
      dominant_sources: [
        "Rainwater tanks on rural acreage — dominant across the Northern Rivers, Mid-North Coast, Southern Highlands and Central West",
        "Sydney Water, Hunter Water and Rous County Council mains supply in urban corridors",
        "Private bores across Central West NSW, the North Coast hinterland and the Riverina",
        "Farm dams and river-fed catchments for stock and irrigation on grazing properties",
      ],
      common_concerns: [
        "PFAS legacy zones near RAAF Base Williamtown and NSW Rural Fire Service training grounds",
        "Iron, manganese and sulphate in shallow coastal-plain bores",
        "Acid-sulfate-soil impact on bore pH along the North Coast and Hunter estuaries",
        "Blue-green algae blooms in warm-season farm dams (Central West and Riverina)",
        "Roof-catchment tank contamination after bushfire (Black Summer) and after the 2022 Lismore floods",
      ],
      regulations: [
        "NSW Public Health Act 2010 and Public Health (Drinking Water) Regulation 2018",
        "NSW Health Private Water Supply Guidelines (rainwater, bore, dam)",
        "Australian Drinking Water Guidelines (ADWG) 2011, updated 2022",
      ],
    },
  },
  victoria: {
    slug: "victoria", country: "australia", name: "Victoria", abbr: "VIC",
    tagline: "From South Gippsland tank properties to Melbourne mains supply and the Murray irrigation country, we test every Victorian water source to Australian Drinking Water Guidelines.",
    regions: ["south-gippsland"],
    context: {
      dominant_sources: [
        "Melbourne Water mains supply across metropolitan Melbourne",
        "Regional urban water corps (Barwon, Coliban, Goulburn Valley, Gippsland Water)",
        "Rainwater tanks on rural properties across Gippsland, the Otways and the Central Highlands",
        "Private bores across the Wimmera, Mallee and irrigation-district properties",
      ],
      common_concerns: [
        "Bushfire-catchment contamination on tanks after major fire events (2009, 2019-20)",
        "Iron, manganese and salinity in Wimmera-Mallee bores",
        "Turbidity spikes on regional-town supply after heavy catchment rain",
        "Blue-green algae on the Murray, Goulburn and Latrobe river systems",
        "Coastal aerosol corrosion on Gippsland and Great Ocean Road tank systems",
      ],
      regulations: [
        "Safe Drinking Water Act 2003 (Vic) and Safe Drinking Water Regulations 2015",
        "Victorian Department of Health private water supply guidance",
        "Australian Drinking Water Guidelines (ADWG) 2011, updated 2022",
      ],
    },
    // Jun 2026 GSC audit — /locations/victoria was the thinnest state hub
    // (~396 words, one active region) and Google crawled it without
    // indexing. This block adds genuine, non-fabricated depth: no
    // invented partner coverage, no invented local statistics. Optional
    // per state — other hubs render exactly as before until filled in.
    extended: {
      intro: [
        "Most Victorians on a licensed town supply have their water monitored before it reaches the meter. Private water is different. If your property draws from a rainwater tank, a bore, a dam or a carted supply, nobody is testing it on your behalf — the quality of that water is entirely the owner's responsibility, and the only way to know what is in it is to have a sample analysed.",
        "That covers a lot of Victorian property. Rainwater tanks are common right across Gippsland, the Otways, the Central Highlands and the Dandenongs. Private bores are widespread through the Wimmera, the Mallee and the northern irrigation districts. Rural and lifestyle blocks frequently run a mix: tank water to the house, bore or dam water for stock, garden and firefighting.",
        "AWARE Water Testing is an independent testing service, not a filter retailer, so the report you receive is simply what your water contains, measured against the Australian Drinking Water Guidelines. We test Victorian samples the same way we test every other sample in the country, and you do not need a technician on site to get it done — a mail-out kit reaches any Victorian address.",
      ],
      process: [
        { title: "Choose your test", body: "Pick the test that matches your source — a general drinking-water screen, a full rainwater-tank profile, or a bore and groundwater profile." },
        { title: "Collect your sample", body: "Your kit arrives with sterile bottles, step-by-step sampling instructions and a prepaid return satchel. Sampling takes a few minutes." },
        { title: "Laboratory analysis", body: "Your sample is analysed in our laboratory and every result is benchmarked against the Australian Drinking Water Guidelines." },
        { title: "Plain-English report", body: "You receive a clear report by email showing each result, whether it sits inside the guideline value, and what it means in practice." },
      ],
      tests: [
        { name: "AWARE 4-Point", href: "/aware-4-point", body: "The core drinking-water screen — a fast, affordable check on the parameters that matter most for a household supply." },
        { name: "AWARE Tank 360", href: "/aware-tank-360", body: "Built for rainwater tanks: bacteriological safety plus the metals and chemistry that roof-and-gutter catchments pick up." },
        { name: "AWARE Bore 360", href: "/aware-bore-360", body: "Built for bores and groundwater, where hardness, iron, manganese and salinity are usually the deciding factors." },
      ],
      guides: [
        { label: "Is rainwater safe to drink?", href: "/blog/is-rainwater-safe-to-drink" },
        { label: "Is bore water safe to drink?", href: "/blog/is-bore-water-safe-to-drink" },
        { label: "How to test water quality", href: "/blog/how-to-test-water-quality" },
        { label: "Understanding the Australian Drinking Water Guidelines", href: "/blog/australian-drinking-water-guidelines" },
        { label: "Water hardness explained", href: "/blog/water-hardness-test" },
        { label: "Heavy metals in water", href: "/blog/heavy-metals-in-water" },
      ],
      faqs: [
        { question: "Do I need a technician to visit my Victorian property?",
          answer: "No. A mail-out sample kit can be posted to any Victorian address — you collect the sample yourself and return it in the prepaid satchel. Where an AWARE or PWS partner operates nearby, an on-site visit can be booked instead." },
        { question: "Is my rainwater tank or bore covered by Victorian water regulations?",
          answer: "The Safe Drinking Water Act 2003 (Vic) sets the standards that licensed water suppliers must meet. It does not extend to a private rainwater tank, bore or dam on your own property, so testing a private supply is the owner's decision and responsibility." },
        { question: "What are Victorian results compared against?",
          answer: "Every result is benchmarked against the Australian Drinking Water Guidelines, the national health-based reference used across all states and territories." },
        { question: "How often should a private Victorian supply be tested?",
          answer: "A yearly test is a sensible baseline for a tank or bore used for drinking, with an extra test after any event that could change the water — a bushfire in the catchment, flooding, a new pump or liner, a long dry spell, or any change in taste, smell or colour." },
      ],
    },
  },
  "northern-territory": {
    slug: "northern-territory", country: "australia", name: "Northern Territory", abbr: "NT",
    tagline: "From Darwin mains supply to Katherine's PFAS legacy zone and remote-community tank and bore water, we test every Territory water source to Australian Drinking Water Guidelines.",
    regions: ["top-end"],
    context: {
      dominant_sources: [
        "Power and Water Corporation mains supply across Darwin, Palmerston and Katherine",
        "Roof-catchment rainwater tanks on remote and rural properties",
        "Private bores across the Top End tropical monsoon zone",
        "Community-managed water supplies in remote Aboriginal communities",
      ],
      common_concerns: [
        "PFAS legacy zone at RAAF Base Tindal near Katherine — nationally documented",
        "Turbidity and biological load in wet-season tank catchments",
        "Iron, manganese and hardness in Top End bores drawn from lateritic profiles",
        "Long dry-season tank stagnation on properties without annual cleaning",
        "Cyclone-flood contamination of tanks and shallow bores after major wet-season events",
      ],
      regulations: [
        "NT Public and Environmental Health Act 2011 and Regulations",
        "NT Department of Health private water supply guidance",
        "Australian Drinking Water Guidelines (ADWG) 2011, updated 2022",
      ],
    },
  },
};

// Regions live under a state. Only `sunshine-coast` (QLD) and
// `northern-rivers` (NSW) are active as pilot regions.
export const REGIONS = {
  "sunshine-coast": {
    slug: "sunshine-coast",
    state: "queensland",
    name: "Sunshine Coast",
    // Per-region hero copy. Written specifically for Sunshine Coast —
    // this is not the framework text — so the pilot has genuinely
    // local content per the SEO quality guardrail.
    tagline:
      "Australia's most water-diverse LGA: hinterland acreage on tanks and bores, coastal households on town supply, and everything in between.",
    // Attribute-based partner discovery (see RegionHub) resolves this
    // via the `by-region/QLD/Sunshine Coast` endpoint at render time,
    // so this hardcoded fallback is intentionally empty. Add slugs
    // here ONLY for cross-region referrals (e.g. a WA partner also
    // covering Sunshine Coast) that shouldn't be discovered by
    // attributes.
    partner_slugs: [],
    // These match `region_cluster` values inside locations.json so we
    // can trivially list suburbs per cluster on the regional hub.
    clusters: [
      {
        key: "noosa-hinterland",
        name: "Noosa & Hinterland",
        summary:
          "Rainwater-tank dominant acreage north of the Noosa River. Sandy soils, occasional bore properties, high summer rainfall. Ideal for annual tank testing after each wet season.",
      },
      {
        key: "coolum-peregian",
        name: "Coolum & Peregian",
        summary:
          "Coastal coastal-strip suburbs blending town-water urban housing with rainwater-tank acreage inland. Coastal iron corrosion on plumbing is common — pH testing is a priority.",
      },
      {
        key: "maroochy-mooloolah",
        name: "Maroochy & Mooloolah Valley",
        summary:
          "Mixed acreage and urban corridor between Buderim and the coast. Bore water is common on properties without town connections; sandy aquifers can yield high iron and manganese.",
      },
      {
        key: "hinterland-glasshouse",
        name: "Sunshine Coast Hinterland — Glasshouse Mountains",
        summary:
          "Elevated acreage from Maleny through Woombye. Basalt-derived soils yield harder bore water; roof-catchment tanks are the primary drinking source on most farms.",
      },
    ],
    // Local water sources — copy is genuinely region-specific.
    water_sources: [
      { key: "rainwater", label: "Rainwater tanks",
        blurb: "Roof-catchment rainwater is the primary drinking source on ~70% of Sunshine Coast acreage. Test annually or after any tank clean." },
      { key: "bore",       label: "Private bore",
        blurb: "Shallow sand-aquifer bores dominate the hinterland corridor. Watch for iron, manganese, and pH extremes." },
      { key: "town",       label: "Unity Water town supply",
        blurb: "Fine chlorine and fluoride management by Unity Water — most concerns are premises-side (hot-water tank sediment, copper corrosion)." },
      { key: "dam",        label: "Dam & creek water",
        blurb: "For irrigation, stock or emergency backup. E. coli and sediment are the main safety concerns before any human use." },
    ],
    // Direct booking-flow guidance for the region page.
    // Booking flow uses the current SSoT test id.
    default_test_slug: "tank",
    // Postcode range covered — used for `data-testid` and copy.
    postcodes_covered_range: "4550 – 4573",
  },
  "northern-rivers": {
    slug: "northern-rivers",
    state: "new-south-wales",
    name: "Northern Rivers",
    // Per-region hero copy. Written specifically for the Northern
    // Rivers of NSW — high-rainfall coastal corner, dominant tank
    // usage, and the post-2022 flood context. Not the framework text.
    tagline:
      "NSW's wettest coastal corner: high-rainfall roof-catchment tanks on the hinterland, acid-sulfate-affected shallow bores on the floodplain, and post-2022-flood contamination that still shows up in test panels today.",
    // Attribute-based discovery resolves this at render time via the
    // `by-region/NSW/Northern Rivers` endpoint. Empty by design —
    // add slugs here ONLY for cross-region referrals.
    partner_slugs: [],
    // Clusters mirror the four Northern Rivers LGAs so we can group
    // suburb pages logically as they're added to locations.json.
    clusters: [
      {
        key: "byron-shire",
        name: "Byron Shire",
        summary:
          "Coastal shire from Byron Bay through Suffolk Park, Mullumbimby, Ocean Shores and Brunswick Heads. Rainwater-tank dominant hinterland acreage plus Rous County Council town supply in the built-up strip. High tank turnover after summer rainfall makes annual testing sensible.",
      },
      {
        key: "ballina-shire",
        name: "Ballina Shire",
        summary:
          "Ballina, Lennox Head, Alstonville and Wardell. Coastal-plain properties often sit on acid-sulfate soils — bore pH can drop below ADWG limits after prolonged wet periods. Rous supplies most townships; tank-dominant properties inland.",
      },
      {
        key: "lismore-region",
        name: "Lismore & Hinterland",
        summary:
          "Lismore, Nimbin, Bangalow, Bexhill and Clunes. The 2022 flood contaminated many roof catchments; tanks that weren't professionally cleaned may still return elevated E. coli. Basalt-plateau bores often show manganese and hardness.",
      },
      {
        key: "tweed-shire",
        name: "Tweed Shire",
        summary:
          "Murwillumbah, Kingscliff, Pottsville, Cabarita Beach and Chinderah. Mixed profile: coastal town-supply, cane-country bores, and rainwater-tank acreage on the Mount Warning caldera. Coastal iron corrosion is a common premises-side finding.",
      },
    ],
    // Local water sources — Northern Rivers specific.
    water_sources: [
      { key: "rainwater", label: "Rainwater tanks",
        blurb: "Dominant supply on rural hinterland — heavy rainfall means fast turnover, so leaf litter, birds and bats keep bacterial risk elevated. Test after every major flood or bushfire event." },
      { key: "bore",       label: "Private bore / spear point",
        blurb: "Shallow sand-aquifer bores on the coastal floodplain sit above acid-sulfate soils — expect low pH, iron, manganese and sulphate. Deeper basalt bores on the plateau tend to run hard." },
      { key: "town",       label: "Rous County Council town supply",
        blurb: "Rous supplies Ballina, Lismore and Byron townships. Chlorine and fluoride are well-managed at source; most customer concerns are premises-side (hot-water sediment, copper corrosion, taste-and-odour)." },
      { key: "dam",        label: "Farm dam & creek water",
        blurb: "Stock, irrigation and emergency backup on Lismore and Kyogle hinterland farms. E. coli and turbidity are the main safety concerns before any human use — test before switching to dam supply in a drought." },
    ],
    // Direct booking-flow guidance — Tank 360 is the highest-fit
    // default for the Northern Rivers rainwater-dominant profile.
    default_test_slug: "tank",
    // Postcode range covered — used for `data-testid` and copy.
    postcodes_covered_range: "2478 – 2484",
  },
  toowoomba: {
    slug: "toowoomba",
    state: "queensland",
    name: "Toowoomba & Darling Downs",
    // Toowoomba sits on the Great Dividing Range at ~700m elevation
    // — cool-climate cropping and grazing country over a deep basalt
    // aquifer. Water character is completely different to the
    // coastal SEQ regions further east.
    tagline:
      "Queensland's high country: deep basalt bores that run hard and manganese-heavy, dam-fed Toowoomba town supply, and a Darling Downs PFAS legacy zone at Oakey that shapes what we test for.",
    partner_slugs: [],
    // Clusters mirror the Toowoomba Regional Council + neighbouring
    // LGAs so suburb pages can be grouped as they're added.
    clusters: [
      {
        key: "toowoomba-city",
        name: "Toowoomba City",
        summary:
          "Toowoomba proper, Highfields, Kearneys Spring, Rangeville. Most homes are on Toowoomba Regional Council supply from Cressbrook and Cooby Dams — safe at source but historically vulnerable to catchment turbidity during drought-break rain.",
      },
      {
        key: "darling-downs-east",
        name: "Darling Downs East",
        summary:
          "Oakey, Pittsworth, Cambooya, Clifton. Cropping and grazing country west of the range. Oakey is a nationally-documented PFAS legacy zone (former RAAF firefighting training area) — testing needs a specialist referral there.",
      },
      {
        key: "southern-downs",
        name: "Southern Downs",
        summary:
          "Warwick, Killarney, Stanthorpe, Allora. Cool-climate high-country. Granite belt bores (Stanthorpe) run soft and slightly acidic; Warwick basalt bores run hard and mineral-heavy. Rainwater tanks dominate on rural acreage.",
      },
    ],
    // Toowoomba-specific water sources.
    water_sources: [
      { key: "town",       label: "Toowoomba Regional Council town supply",
        blurb: "Sourced from Cressbrook, Cooby and Perseverance Dams. Historically vulnerable to turbidity and taste-and-odour events after major catchment rainfall — Toowoomba manages the treatment response. Chlorine and fluoride are reliably at spec at the plant." },
      { key: "bore",       label: "Deep basalt bore",
        blurb: "The Darling Downs basalt aquifer sits under most of this region. Bore water is typically hard, mineral-rich and iron/manganese-heavy — the basalt is dissolving into the groundwater over geological time. Fluoride can also be elevated in some deep bores." },
      { key: "rainwater",  label: "Rainwater tanks",
        blurb: "Common on rural blocks across the Downs. Lower rainfall than coastal Queensland means tanks turn over more slowly, so annual testing catches microbiology drift, bird/bat ingress and long-storage stagnation." },
      { key: "dam",        label: "Farm dam & bore-fed dam",
        blurb: "Widespread for stock, irrigation and emergency backup. Blue-green algae blooms are a real summer concern on the western Downs — visible surface scum is a hard signal to stop human contact and get a targeted test." },
    ],
    default_test_slug: "bore",
    postcodes_covered_range: "4350 – 4413",
  },
  "riverina-southern-tablelands": {
    slug: "riverina-southern-tablelands",
    state: "new-south-wales",
    name: "Riverina & Southern Tablelands",
    tagline:
      "Big country, big contrasts: Murrumbidgee irrigation-area bores that can push nitrate and salinity, Southern Tablelands rainwater tanks and basalt bores, and blue-green algae watch across the summer river systems.",
    partner_slugs: [],
    // Clusters follow the three natural sub-regions.
    clusters: [
      {
        key: "riverina-central",
        name: "Riverina Central",
        summary:
          "Wagga Wagga, Junee, Cootamundra, Coolamon. Deep alluvial bores plus mixed town supply. Wagga is a nationally-documented PFAS legacy zone near the RAAF base — testing needs a specialist referral in the flagged catchment.",
      },
      {
        key: "riverina-mia",
        name: "Murrumbidgee Irrigation Area",
        summary:
          "Griffith, Leeton, Yenda, Hillston. Intensive irrigation country — rice, cotton, citrus, wine. Shallow bores here can pick up nitrate from cropping and salinity from irrigation returns; a proper baseline test matters before any household use.",
      },
      {
        key: "southern-tablelands",
        name: "Southern Tablelands",
        summary:
          "Goulburn, Yass, Crookwell, Braidwood, Bungendore. Cool high country with basalt-plateau bores that often run hard, and rainwater tanks on the rural blocks that dominate outside the town centres. Long dry seasons mean tanks sit for months between refills.",
      },
    ],
    water_sources: [
      { key: "town",       label: "Council or Riverina Water town supply",
        blurb: "Wagga, Griffith, Leeton, Yass and Goulburn are on council-managed treated town supply. Historically stable at the plant; premises-side issues (chlorine sediment, hot-water fouling) are the most common customer concerns." },
      { key: "bore",       label: "Deep alluvial or basalt bore",
        blurb: "Riverina bores draw from the Murrumbidgee alluvium — nitrate and salinity are the standout parameters worth testing. Southern Tablelands bores tap basalt — hardness, iron and manganese dominate the chemistry." },
      { key: "rainwater",  label: "Rainwater tanks",
        blurb: "Widespread on rural properties across the whole region. Lower rainfall than coastal NSW means slower tank turnover and longer stagnation periods — test annually plus after any bushfire or dust storm." },
      { key: "dam",        label: "Farm dam & river diversion",
        blurb: "Stock, irrigation and emergency household backup across the region. Summer blue-green algae blooms on the Murrumbidgee and Murray river systems are a real concern — visible surface scum means stop human contact and test." },
    ],
    default_test_slug: "bore",
    postcodes_covered_range: "2580 – 2705",
  },
  "bathurst-central-tablelands": {
    slug: "bathurst-central-tablelands",
    state: "new-south-wales",
    name: "Bathurst & Central Tablelands",
    // Jun 2026 — cold high country west of the Blue Mountains: granite
    // and basalt geology, frost-prone tank country, and three distinct
    // council water schemes (Bathurst Regional, Orange City, Fish River).
    tagline:
      "Cold-climate high country west of the Blue Mountains: granite-belt bores around Bathurst, rich basalt soils and orchard bores under Mount Canobolas, Fish River scheme towns, and frost-country rainwater tanks that sit for months between refills.",
    partner_slugs: [],
    clusters: [
      {
        key: "bathurst-region",
        name: "Bathurst Region",
        summary:
          "Bathurst, Kelso, Eglinton, Perthville, O'Connell, Rockley. Bathurst Regional Council supplies town water from Chifley Dam on the Campbells River; rural blocks on the granite belt run rainwater tanks and shallow granite bores that tend soft, slightly acidic and iron-stained.",
      },
      {
        key: "orange-cabonne",
        name: "Orange & Cabonne",
        summary:
          "Orange, Millthorpe, Blayney, Molong, Canowindra, Cudal. Orange draws from Suma Park Dam with stormwater harvesting top-up. The Mount Canobolas basalt gives rich volcanic soils — orchard and vineyard bores here run hard with iron and manganese worth baselining before household use.",
      },
      {
        key: "lithgow-oberon",
        name: "Lithgow & Oberon",
        summary:
          "Lithgow, Portland, Wallerawang, Oberon, Black Springs, Tarana. The coldest country in the region — Fish River Water Scheme supplies the towns, while pine-forest and grazing blocks rely on tanks and fractured-sandstone bores. Lithgow's coal-mining legacy makes a groundwater baseline test worthwhile.",
      },
    ],
    water_sources: [
      { key: "town",       label: "Council & Fish River scheme town supply",
        blurb: "Bathurst Regional (Chifley Dam), Orange City (Suma Park Dam) and the Fish River Water Scheme (Oberon Dam, serving Lithgow, Wallerawang, Portland and Oberon) all treat to spec at the plant. Most customer concerns are premises-side — hot-water sediment, copper corrosion after frost damage, taste and odour." },
      { key: "bore",       label: "Granite or basalt bore",
        blurb: "Two very different chemistries side by side: granite-belt bores around Bathurst and Oberon run soft, slightly acidic and iron-stained, while Mount Canobolas basalt bores around Orange run hard with manganese. A baseline test tells you which maintenance path your bore needs." },
      { key: "rainwater",  label: "Rainwater tanks",
        blurb: "Dominant on rural blocks across the whole Tablelands. Cold winters and modest rainfall mean slow tank turnover and long stagnation — test annually, plus after bushfire ash events on the Blue Mountains fringe and any spring dust storm." },
      { key: "dam",        label: "Farm dam & creek water",
        blurb: "Stock and irrigation storage across the grazing country, with summer blue-green algae watch on the Macquarie (Wambuul) River system and warm shallow dams. Visible surface scum means stop human and stock contact and get a targeted test." },
    ],
    default_test_slug: "tank",
    postcodes_covered_range: "2786 – 2880",
  },
  "dayboro": {
    slug: "dayboro", state: "queensland", name: "Dayboro & Moreton Bay Hinterland",
    tagline: "Rural acreage on Brisbane's northern doorstep — rainwater tanks and volcanic-plug bores on the D'Aguilar Range fringe, with mail-order kits for every household off the town-water grid.",
    partner_slugs: [],
    clusters: [
      { key: "dayboro-village", name: "Dayboro Village", summary: "Dayboro township, Kurwongbah, Mount Mee foothills. Small-town mix of tank properties and Unitywater mains supply." },
      { key: "samford-valley", name: "Samford Valley", summary: "Samford, Camp Mountain, Cedar Creek. Acreage country — rainwater tanks dominate on rural blocks; bores tap the volcanic-plug aquifer." },
      { key: "kilcoy-hinterland", name: "Kilcoy Hinterland", summary: "Kilcoy, Woodford, Mount Kilcoy. Grazing and small-cropping country — tank plus dam plus occasional bore." },
    ],
    water_sources: [
      { key: "rainwater", label: "Rainwater tanks", blurb: "The dominant supply on Moreton Bay hinterland acreage — subtropical rainfall keeps tanks turning over but leaf litter and wildlife loading are annual concerns." },
      { key: "bore", label: "Volcanic-plug bore", blurb: "The D'Aguilar Range volcanic-plug aquifer produces hard, mineral-rich bore water — hardness, iron and manganese dominate the chemistry." },
      { key: "town", label: "Unitywater town supply", blurb: "Dayboro township and parts of Samford are on Unitywater mains — safe at the source, premises-side issues are the most common customer concern." },
      { key: "dam", label: "Farm dams", blurb: "Widespread for stock and irrigation. Blue-green algae watch during warm dry stretches." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "4506 – 4521",
  },
  "redland-bay-logan": {
    slug: "redland-bay-logan", state: "queensland", name: "Redland Bay & Logan",
    tagline: "Bayside suburbia to the Moreton Bay islands: SEQ Water mains dominates the mainland, but Russell, Macleay and Karragarra islanders rely on tanks and bores that need annual eyes-on.",
    partner_slugs: [],
    clusters: [
      { key: "redlands", name: "Redlands", summary: "Cleveland, Victoria Point, Redland Bay, Wellington Point. Suburban SEQ Water mains supply with tank-supplemented eco-builds." },
      { key: "moreton-bay-islands", name: "Moreton Bay Islands", summary: "Russell, Macleay, Lamb, Karragarra, Coochiemudlo. Island properties largely off the mains grid — rainwater tanks and shallow bores dominate." },
      { key: "logan-north", name: "Logan North", summary: "Springwood, Beenleigh, Loganholme, Waterford. Mixed suburban and light-rural — SEQ Water mains plus tank-supplemented acreage south of the M1." },
    ],
    water_sources: [
      { key: "town", label: "SEQ Water / Urban Utilities mains supply", blurb: "The dominant supply across mainland Redlands and Logan — treated to ADWG at the SEQ treatment network. Premises-side issues (chlorine sediment, hot-water fouling) are the main customer concerns." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "Compulsory on all new SEQ dwellings under the 2007 tank rebate legislation, and the primary supply on Moreton Bay island properties. Annual testing tracks microbiology drift and any coastal-aerosol iron creep." },
      { key: "bore", label: "Shallow bore", blurb: "Some Moreton Bay island properties and southern Logan acreage run shallow bores — expect brackish influence on the islands and iron/manganese elsewhere." },
      { key: "dam", label: "Farm dams", blurb: "Rural blocks in southern Logan use dams for stock and irrigation — human-contact use warrants a Basic Water Safety Check baseline." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "4157 – 4310",
  },
  "tweed-byron": {
    slug: "tweed-byron", state: "new-south-wales", name: "Tweed / Byron Border Coast",
    tagline: "Border-coast franchise territory spanning the Tweed and Byron catchments — from Tweed Heads and Kingscliff south to Byron Bay and Mullumbimby. Mail-out kits and local sample handling.",
    partner_slugs: [],
    clusters: [
      { key: "border-coastal", name: "Border Coastal", summary: "Tweed Heads, Banora Point, Kingscliff, Cabarita. Coastal aerosol corrosion and mixed mains-plus-tank supply — Tweed Shire Council water on the mainland strip." },
      { key: "tweed-valley-hinterland", name: "Tweed Valley Hinterland", summary: "Murwillumbah, Uki, Chillingham. Wollumbin caldera bore chemistry — hard, iron- and manganese-rich; tank properties on acreage." },
      { key: "byron-hinterland", name: "Byron Hinterland", summary: "Byron Bay, Suffolk Park, Mullumbimby, Ocean Shores. Flying-fox bat overflights and dense subtropical canopy drive tank microbiological risk." },
    ],
    water_sources: [
      { key: "town", label: "Council mains supply", blurb: "Tweed Shire Council (Bray Park Weir / Clarrie Hall Dam) supplies the built-up coastal strip; Rous County Council (Rocky Creek Dam) reaches into the Byron hinterland townships." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "Coastal aerosol corrosion accelerates iron and manganese staining; hinterland canopies drive higher microbiological load year-round." },
      { key: "bore", label: "Caldera bore", blurb: "Wollumbin caldera bore water is often hard, mineral-rich and iron-heavy — the same basalt aquifer signature as central Murwillumbah." },
      { key: "dam", label: "Farm dams", blurb: "Stock and irrigation on hinterland acreage — human-contact use warrants annual microbiology screening." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "2479 – 2490",
  },
  "central-coast": {
    slug: "central-coast", state: "new-south-wales", name: "Central Coast NSW",
    tagline: "Gosford, Wyong and the coastal lakes: Central Coast Council mains supply for the majority, plus tank-and-bore properties across the Mangrove Mountain plateau and rural fringe.",
    partner_slugs: [],
    clusters: [
      { key: "gosford-region", name: "Gosford Region", summary: "Gosford, East Gosford, Woy Woy, Umina Beach. Central Coast Council mains supply from Mangrove Creek Dam; premises-side plumbing issues dominate customer concerns." },
      { key: "wyong-lakes", name: "Wyong & The Lakes", summary: "Wyong, The Entrance, Toukley, Long Jetty. Council mains supply through the townships; coastal aerosol corrosion on tanked properties." },
      { key: "central-coast-plateau", name: "Central Coast Plateau", summary: "Mangrove Mountain, Somersby, Kulnura. Rural acreage — rainwater tanks and shallow bores are the norm on plateau blocks." },
    ],
    water_sources: [
      { key: "town", label: "Central Coast Council supply", blurb: "Mangrove Creek Dam and the Mardi-Mangrove system supply the majority. Historically stable, with wet-season turbidity managed at the plant." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "Dominant on the Mangrove Mountain plateau and rural-residential blocks. Coastal aerosols nudge conductivity up on beach-adjacent tanks." },
      { key: "bore", label: "Plateau bore", blurb: "Mangrove Mountain and Somersby plateau bores tap Hawkesbury sandstone — expect iron staining and occasional hardness." },
      { key: "dam", label: "Farm dams", blurb: "Stock, irrigation and emergency backup on the plateau. Basic Water Safety Check for human-contact use." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "2250 – 2263",
  },
  "tropical-north-queensland": {
    slug: "tropical-north-queensland", state: "queensland", name: "Tropical North Queensland",
    tagline: "Cairns, Port Douglas and the Wet Tropics: rainforest-catchment tanks, Barron River town supply, Tablelands basalt bores, and a cyclone-and-flood context that resets tank chemistry every wet season.",
    partner_slugs: [],
    clusters: [
      { key: "cairns-region", name: "Cairns Region", summary: "Cairns, Edmonton, Trinity Beach, Palm Cove. Cairns Regional Council supply from the Behana Gorge and Copperlode Falls dams; tank-supplemented eco-builds along the northern beaches." },
      { key: "port-douglas-daintree", name: "Port Douglas & Daintree", summary: "Port Douglas, Mossman, Daintree, Cape Tribulation. Off-grid tank properties dominate north of the Daintree River — annual testing plus post-cyclone testing recommended." },
      { key: "atherton-tablelands", name: "Atherton Tablelands", summary: "Atherton, Malanda, Yungaburra, Ravenshoe. Cool-climate basalt-plateau bores and Tablelands Regional Council supply; rainwater tanks on grazing and cropping blocks." },
    ],
    water_sources: [
      { key: "town", label: "Cairns Regional / Tablelands Regional Council supply", blurb: "Cairns draws from Behana Gorge and Copperlode Falls Dam. Wet-season turbidity spikes are a recurring seasonal issue managed at the plant." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "Dominant north of the Daintree and on rural Tablelands blocks. High wet-season rainfall means fast turnover but heavy leaf-litter and wildlife load — annual testing is a genuine must." },
      { key: "bore", label: "Basalt-plateau bore", blurb: "Atherton Tablelands bores tap volcanic basalt — hardness, iron and manganese dominate. Some deep bores show elevated fluoride." },
      { key: "dam", label: "Farm dams", blurb: "Stock and irrigation across the Tablelands and Cairns hinterland. Blue-green algae watch during warm dry stretches." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "4870 – 4895",
  },
  "top-end": {
    slug: "top-end", state: "northern-territory", name: "Top End",
    tagline: "Darwin, Palmerston, Katherine and the surrounding wet-dry tropics — Power and Water mains supply, off-grid tanks and bores that face PFAS legacy zones and cyclone-flood recovery every season.",
    partner_slugs: [],
    clusters: [
      { key: "darwin-region", name: "Darwin & Palmerston", summary: "Darwin, Palmerston, Humpty Doo, Howard Springs. Power and Water Corporation mains supply plus rural-residential tank properties on the fringe." },
      { key: "katherine-region", name: "Katherine Region", summary: "Katherine, Tindal, Pine Creek. Nationally documented PFAS legacy zone near RAAF Base Tindal — testing needs a specialist referral in the flagged catchment." },
      { key: "tiwi-arnhem", name: "Tiwi & Arnhem", summary: "Tiwi Islands, Wadeye, remote East Arnhem communities. Community-managed water supplies plus tanks — mail-out kits ship Territory-wide." },
    ],
    water_sources: [
      { key: "town", label: "Power and Water Corporation supply", blurb: "Darwin, Palmerston and Katherine are on treated mains water. Wet-season turbidity events are managed at the plant, but premises-side hot-water sediment is common." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "The dominant supply on remote and rural properties — wet-season rainfall floods catchments but the long dry-season stagnation is the bigger risk for annual testing." },
      { key: "bore", label: "Lateritic bore", blurb: "Top End bores drawn from lateritic profiles run iron- and manganese-heavy. Katherine catchment bores need PFAS-specific consideration." },
      { key: "dam", label: "Community dam / river diversion", blurb: "Some remote communities rely on managed dam or river-diversion supplies — infrastructure varies significantly." },
    ],
    default_test_slug: "bore", postcodes_covered_range: "0800 – 0862",
  },
  "gold-coast-south-north": {
    slug: "gold-coast-south-north", state: "queensland", name: "Gold Coast (South & North)",
    tagline: "From Surfers to Coolangatta and up through Nerang and the hinterland — Gold Coast Water mains dominates the beach strip, but tank-supplemented eco-builds and hinterland bores need their own eyes-on.",
    partner_slugs: [],
    clusters: [
      { key: "gold-coast-north", name: "North Gold Coast", summary: "Southport, Surfers Paradise, Broadbeach, Runaway Bay. Dense urban with Gold Coast Water mains supply; premises-side plumbing issues dominate customer concerns." },
      { key: "gold-coast-south", name: "South Gold Coast", summary: "Burleigh, Palm Beach, Currumbin, Coolangatta, Tugun. Coastal aerosol corrosion accelerates tank iron staining; some hinterland blocks run bore and tank." },
      { key: "gold-coast-hinterland", name: "Gold Coast Hinterland", summary: "Nerang, Mudgeeraba, Springbrook, Tamborine. Rural acreage on rainwater tanks and volcanic bores — Springbrook plateau chemistry is distinctive." },
    ],
    water_sources: [
      { key: "town", label: "Gold Coast Water mains supply", blurb: "Hinze Dam is the primary source — treated to ADWG at the plant. Chlorine and hot-water sediment complaints are the common premises-side issues." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "Compulsory on new dwellings; coastal aerosols accelerate iron staining. Hinterland tanks see subtropical microbiological load." },
      { key: "bore", label: "Volcanic hinterland bore", blurb: "Springbrook, Tamborine and Numinbah bores tap volcanic aquifers — hardness, iron and manganese dominate." },
      { key: "dam", label: "Farm dams", blurb: "Rural blocks in the hinterland use dams for stock and irrigation — human-contact use warrants a Basic Water Safety Check." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "4211 – 4230",
  },
  "cooloola-fraser-coast": {
    slug: "cooloola-fraser-coast", state: "queensland", name: "Cooloola & Fraser Coast",
    tagline: "Rainbow Beach to Hervey Bay, plus K'gari (Fraser Island): sand-island tank-only supply, cyclone-and-flood catchments, and Fraser Coast Regional Council mains through the urban strip.",
    partner_slugs: [],
    clusters: [
      { key: "cooloola-coast", name: "Cooloola Coast", summary: "Rainbow Beach, Tin Can Bay, Cooloola Cove. Coastal aerosol corrosion and mixed tank-plus-mains supply; K'gari-adjacent tourism properties on tank-only." },
      { key: "hervey-bay-region", name: "Hervey Bay Region", summary: "Hervey Bay, Urangan, Point Vernon. Fraser Coast Regional Council mains supply; premises-side plumbing issues dominate." },
      { key: "kgari-fraser-island", name: "K'gari (Fraser Island)", summary: "Kingfisher Bay, Eurong, Happy Valley. Sand-island tank-only supply — no mains water at all. Annual professional tank cleaning plus testing strongly recommended." },
    ],
    water_sources: [
      { key: "town", label: "Fraser Coast Regional Council supply", blurb: "Hervey Bay and the urban Fraser Coast are on treated mains water from council sources. Wet-season turbidity events are managed at the plant." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "The dominant and often ONLY supply on K'gari and coastal-fringe properties. Sand-island properties see fast tank turnover but distinctive salinity influence from coastal aerosols." },
      { key: "bore", label: "Sand-aquifer bore", blurb: "Some Cooloola Coast and mainland Fraser Coast bores draw from coastal sand aquifers — expect variable chloride and iron depending on proximity to the coast." },
      { key: "dam", label: "Farm dams", blurb: "Rural mainland blocks use dams for stock and irrigation — Basic Water Safety Check for human-contact use." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "4570 – 4655",
  },
  "south-gippsland": {
    slug: "south-gippsland", state: "victoria", name: "South Gippsland",
    tagline: "From Wilson's Promontory to the Bass Coast: rainwater tanks on rural blocks, South Gippsland Water and Westernport Water mains through the townships, and coastal aerosol corrosion on every beach-adjacent catchment.",
    partner_slugs: [],
    clusters: [
      { key: "bass-coast", name: "Bass Coast", summary: "Wonthaggi, Inverloch, Cape Paterson, Cowes. Westernport Water mains supply through townships; tank-supplemented coastal eco-builds." },
      { key: "south-gippsland-inland", name: "South Gippsland Inland", summary: "Leongatha, Korumburra, Foster, Mirboo North. Dairy country — South Gippsland Water mains plus rural blocks on rainwater tanks." },
      { key: "corner-inlet", name: "Corner Inlet & Prom Coast", summary: "Foster, Fish Creek, Waratah Bay, Sandy Point. Rural acreage and Wilson's Prom-adjacent tourism blocks — largely tank supply." },
    ],
    water_sources: [
      { key: "town", label: "South Gippsland / Westernport Water supply", blurb: "Two regional water corps supply the townships from managed catchments. Occasional wet-season turbidity, mostly stable." },
      { key: "rainwater", label: "Rainwater tanks", blurb: "The dominant supply on rural South Gippsland blocks. Lower rainfall than tropical regions means slower turnover — annual testing is the sensible baseline." },
      { key: "bore", label: "Sedimentary basin bore", blurb: "Some inland blocks run shallow bores drawing from South Gippsland's sedimentary basin — expect iron and hardness signatures." },
      { key: "dam", label: "Farm dams", blurb: "Widespread across the dairy country — irrigation and stock use dominates. Blue-green algae watch during warm dry stretches." },
    ],
    default_test_slug: "tank", postcodes_covered_range: "3945 – 3996",
  },
};

/**
 * Normalise a `locations.json` slug (e.g. "doonan-water-testing") to
 * the short canonical suburb slug used in the new hierarchy
 * (e.g. "doonan"). Trailing `-water-testing` is removed.
 */
export function suburb_short_slug_of(fullSlug) {
  if (!fullSlug) return "";
  return String(fullSlug).replace(/-water-testing$/i, "");
}

export function long_slug_from_short(shortSlug) {
  if (!shortSlug) return "";
  // If they already passed the long form, keep it.
  if (/-water-testing$/i.test(shortSlug)) return shortSlug;
  return `${shortSlug}-water-testing`;
}

/**
 * Return the canonical hierarchical path for a suburb given its
 * locations.json entry. Falls back to the legacy path if the entry
 * cannot be classified (no region_cluster).
 */
export function canonical_suburb_path(locationEntry) {
  if (!locationEntry) return "/locations";
  const short = suburb_short_slug_of(locationEntry.slug);
  const cluster = locationEntry.region_cluster;
  const region = Object.values(REGIONS).find((r) =>
    r.clusters.some((c) => c.key === cluster),
  );
  if (!region) return `/locations/${locationEntry.slug}`;
  const state = STATES[region.state];
  return `/locations/${state.slug}/${region.slug}/${short}`;
}
